import { PlusOutlined, QrcodeOutlined, ReloadOutlined } from '@ant-design/icons';
import { AccountQrModal } from '@hq/account';
import type { AdminPermRow, AdminRoleRow, AdminUserRow, HqApi } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { sumWidth, TipText } from '@hq/ui-kit';
import { formatDisplayTime, LEVEL_LABELS, levelLabel } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Drawer,
  Form,
  Input,
  Modal,
  Segmented,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';

const PAGE_SIZE = 20;

/** 特权等级：非平台管理员不能自助分配（与后端 PrivilegedLevelCodes 同口径）。 */
const PRIVILEGED_LEVELS = ['admin', 'sys'];

const STATUS_META: Record<string, { text: string; color: string }> = {
  ENABLED: { text: '启用', color: 'green' },
  DISABLED: { text: '停用', color: 'default' },
  LOCKED: { text: '锁定', color: 'red' },
  FROZEN: { text: '冻结', color: 'orange' }
};

type StatusFilter = 'ALL' | 'ENABLED' | 'DISABLED';

interface CreateValues {
  realName?: string;
  mobile?: string;
  /** 必填：用于重设密码。 */
  email: string;
  /** 机构 id（下拉选中值，统一用 string 避免精度丢失）。 */
  orgId: string;
  levelCode?: string;
  password: string;
  status?: string;
  remark?: string;
}

export interface SystemUsersPageProps {
  hq: HqApi;
  /**
   * 平台视角（web-system）：多「租户」筛选与列，机构快搜按租户收口。
   * 运营端固定本租户，不展示租户维度。
   */
  platform?: boolean;
  /** 当前登录者等级（决定能否分配 admin/sys 这类特权等级）。 */
  viewerLevel?: string | null;
}

/**
 * 用户与权限：用户列表、新建、重置口令、身份码、角色分配、角色权限配置。
 *
 * ops / system 曾各复制一份并分别演进（一边补了身份码、一边补了租户维度），
 * 这里取并集：`platform` 控制租户维度，其余行为完全一致。
 */
export function SystemUsersPage({ hq, platform = false, viewerLevel }: SystemUsersPageProps) {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();

  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState<StatusFilter>('ALL');
  // 平台视角：按租户筛选，未选表示全部租户（platformAll 决定可见范围）
  const [tenantId, setTenantId] = useState<string | undefined>();
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm<CreateValues>();
  /** 邮箱查重只在失焦时触发（避免逐字符发请求）。 */
  const [emailCheck, setEmailCheck] = useState<string | undefined>();
  const [roleUser, setRoleUser] = useState<AdminUserRow | null>(null);
  /** 身份码弹窗目标（App 扫码确认送达/签收，30 天有效）。 */
  const [qrUser, setQrUser] = useState<AdminUserRow | null>(null);
  const [permRole, setPermRole] = useState<AdminRoleRow | null>(null);
  const [selectedPerms, setSelectedPerms] = useState<string[]>([]);

  const users = useInfiniteQuery({
    queryKey: ['sys', 'users', keyword, status, platform ? tenantId : undefined],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.admin.users.list({
        lastId: pageParam,
        size: PAGE_SIZE,
        keyword: keyword || undefined,
        status: status === 'ALL' ? undefined : status,
        tenantId: platform ? tenantId : undefined
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const roles = useQuery({ queryKey: ['sys', 'roles'], queryFn: () => hq.admin.roles.list() });
  const perms = useQuery({ queryKey: ['sys', 'perms'], queryFn: () => hq.admin.perms.list() });

  const userRoles = useQuery({
    queryKey: ['sys', 'user-roles', roleUser?.id],
    placeholderData: keepPreviousData,
    queryFn: () => hq.admin.users.roles(roleUser?.id ?? ''),
    enabled: Boolean(roleUser)
  });

  const tenantOptions = useQuery({
    queryKey: ['sys', 'tenant', 'options'],
    queryFn: () => hq.tenant.list({}),
    enabled: platform,
    staleTime: 5 * 60 * 1000
  });

  const isPlatformAdmin = PRIVILEGED_LEVELS.includes(viewerLevel ?? '');

  // 机构快搜：选项接口登录即可读，不需要 sys:org:manage；平台侧跟租户筛选走，避免 200 条拉不全
  const orgs = useQuery({
    queryKey: ['sys', 'org-options', platform ? tenantId : undefined],
    queryFn: () => hq.org.options(platform ? { tenantId } : undefined),
    enabled: createOpen,
    staleTime: 60 * 1000
  });

  const pickedOrgId = Form.useWatch('orgId', createForm);

  // 账号预览**不占号**，真正取号在提交时由服务端完成
  const preflight = useQuery({
    queryKey: ['sys', 'user-preflight', pickedOrgId, emailCheck],
    queryFn: () => hq.admin.users.preflight({ orgId: pickedOrgId, email: emailCheck }),
    enabled: createOpen && Boolean(pickedOrgId)
  });

  const emailCount = preflight.data?.emailCount ?? 0;
  const codeHint =
    pickedOrgId && preflight.data && !preflight.data.userCode
      ? '该机构无法生成账号（未配机构号或序号已用满），请先修正机构信息'
      : '机构号 + 4 位序号自动生成，不可修改';

  const orgOptions = (orgs.data ?? []).map((org) => ({
    value: String(org.id),
    label: `${org.orgName}（${org.orgCode}）`
  }));

  const levelOptions = Object.entries(LEVEL_LABELS)
    .filter(([code]) => isPlatformAdmin || !PRIVILEGED_LEVELS.includes(code))
    .map(([code, text]) => ({ value: code, label: text }));

  const createUser = useMutation({
    // 账号由服务端按「机构号 + 4 位序号」生成，前端不传 userCode
    mutationFn: (values: CreateValues) =>
      hq.admin.users.create({
        realName: values.realName,
        mobile: values.mobile,
        email: values.email,
        orgId: values.orgId,
        levelCode: values.levelCode,
        password: values.password,
        status: values.status,
        remark: values.remark
      }),
    onSuccess: async (result) => {
      if (result.emailDuplicated) {
        message.warning(`用户已创建：${result.userCode}（该邮箱已被其他账号使用，邮箱用于重设密码，建议唯一）`);
      } else {
        message.success(`用户已创建：${result.userCode}`);
      }
      setCreateOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['sys', 'users'] });
      // 新账号占了一个序号，预览缓存必须作废
      await queryClient.invalidateQueries({ queryKey: ['sys', 'user-preflight'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '创建失败');
    }
  });

  const resetPassword = useMutation({
    mutationFn: (payload: { id: string; password: string }) =>
      hq.admin.users.resetPassword(payload.id, payload.password),
    onSuccess: () => message.success('口令已重置'),
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '重置失败')
  });

  const setRoles = useMutation({
    mutationFn: (payload: { id: string; roleIds: string[] }) =>
      hq.admin.users.setRoles(payload.id, payload.roleIds),
    onSuccess: async () => {
      message.success('角色已更新');
      await queryClient.invalidateQueries({ queryKey: ['sys', 'user-roles'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败')
  });

  const setPerms = useMutation({
    mutationFn: (payload: { id: string; permIds: string[] }) => hq.admin.roles.setPerms(payload.id, payload.permIds),
    onSuccess: async () => {
      message.success('权限已保存，相关用户需重新登录生效');
      setPermRole(null);
      await queryClient.invalidateQueries({ queryKey: ['sys', 'role-perms'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败')
  });

  const rows = users.data?.pages.flatMap((page) => page.items) ?? [];
  const allRoles = roles.data ?? [];
  const allPerms = perms.data ?? [];
  const boundRoleIds = new Set((userRoles.data ?? []).map((role) => role.id));

  const openPerms = async (role: AdminRoleRow) => {
    setPermRole(role);
    const data = await hq.admin.roles.perms(role.id);
    setSelectedPerms(data.map(String));
  };

  const resetWithPrompt = (row: AdminUserRow) => {
    let value = '';
    modal.confirm({
      title: `重置口令：${row.userCode}`,
      content: (
        <Input.Password
          placeholder="新口令（8~64 位）"
          maxLength={64}
          autoComplete="new-password"
          onChange={(event) => {
            value = event.target.value;
          }}
        />
      ),
      okText: '重置',
      cancelText: '取消',
      onOk: () => {
        // 与后端 ValidationRules.EnsurePassword 同口径：8~64 位
        if (value.length < 8 || value.length > 64) {
          message.warning('口令需 8~64 位');
          return Promise.reject(new Error('invalid'));
        }
        return resetPassword.mutateAsync({ id: row.id, password: value });
      }
    });
  };

  const userColumns: ColumnsType<AdminUserRow> = [
    { title: '账号', dataIndex: 'userCode', width: 150 },
    { title: '姓名', dataIndex: 'realName', width: 120, render: (value: string | null) => value || '-' },
    { title: '手机号', dataIndex: 'mobile', width: 140, render: (value: string | null) => value || '-' },
    ...(platform
      ? [
          {
            // 平台跨租户维护：标明账号属于哪个租户，避免误操作
            title: '所属租户',
            dataIndex: 'tenantName',
            width: 150,
            render: (value: string | null, row) => value ?? (row.tenantId === '0' ? '平台' : `#${row.tenantId}`)
          } as ColumnsType<AdminUserRow>[number]
        ]
      : []),
    {
      title: '所属机构',
      dataIndex: 'orgName',
      width: 200,
      ellipsis: { showTitle: false },
      render: (value: string | null, row) => <TipText text={value || `机构 ${row.orgId}`} />
    },
    { title: '等级', dataIndex: 'levelCode', width: 120, render: (value: string | null) => levelLabel(value) },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (value: string | null) => {
        const meta = STATUS_META[value ?? ''] ?? { text: value ?? '-', color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '最近登录', dataIndex: 'lastLoginAt', width: 160, render: (value: string | null) => formatDisplayTime(value) },
    {
      title: '操作',
      key: 'action',
      width: 300,
      fixed: 'right',
      render: (_value: unknown, row) => (
        <Space>
          <Button type="link" onClick={() => setRoleUser(row)}>
            角色
          </Button>
          <Button type="link" onClick={() => resetWithPrompt(row)}>
            重置口令
          </Button>
          <Button type="link" icon={<QrcodeOutlined />} onClick={() => setQrUser(row)}>
            身份码
          </Button>
        </Space>
      )
    }
  ];

  const roleColumns: ColumnsType<AdminRoleRow> = [
    { title: '角色编码', dataIndex: 'roleCode', width: 180 },
    { title: '名称', dataIndex: 'roleName', width: 160, render: (value: string | null) => value || '-' },
    { title: '门户', dataIndex: 'portal', width: 110, render: (value: string | null) => value || '-' },
    {
      title: '数据范围',
      dataIndex: 'dataScope',
      width: 130,
      render: (value: string | null) =>
        value === 'ALL' || value === 'REGION' ? <Tag color="purple">{value}</Tag> : <Tag>{value ?? '-'}</Tag>
    },
    { title: '内置', dataIndex: 'isBuiltin', width: 80, render: (value: boolean) => (value ? '是' : '否') },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_value: unknown, row) => (
        <Button type="link" onClick={() => void openPerms(row)}>
          配置权限
        </Button>
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title="用户与权限"
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => void users.refetch()} loading={users.isFetching}>
            刷新
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              createForm.resetFields();
              setEmailCheck(undefined);
              setCreateOpen(true);
            }}
          >
            新建用户
          </Button>
        </Space>
      }
    >
      <AccountQrModal
        hq={hq}
        open={Boolean(qrUser)}
        userId={qrUser?.id ?? ''}
        userName={qrUser?.realName ?? qrUser?.userCode}
        onClose={() => setQrUser(null)}
      />

      <Tabs
        items={[
          {
            key: 'users',
            label: '用户',
            children: (
              <>
                <Space style={{ marginBottom: 12 }} wrap>
                  {platform ? (
                    <Select
                      showSearch
                      optionFilterProp="label"
                      placeholder="全部租户"
                      style={{ width: 220 }}
                      value={tenantId}
                      onChange={(value) => setTenantId(value ?? undefined)}
                      options={(tenantOptions.data ?? []).map((item) => ({
                        value: String(item.id),
                        label: `${item.tenantName}（${item.tenantCode}）`
                      }))}
                      allowClear
                    />
                  ) : null}
                  <Input.Search
                    placeholder="账号 / 姓名 / 手机号"
                    allowClear
                    style={{ width: 260 }}
                    onSearch={(value) => setKeyword(value)}
                  />
                  <Segmented<StatusFilter>
                    value={status}
                    options={[
                      { label: '全部', value: 'ALL' },
                      { label: '启用', value: 'ENABLED' },
                      { label: '停用', value: 'DISABLED' }
                    ]}
                    onChange={(value) => setStatus(value)}
                  />
                </Space>

                {users.isError ? (
                  <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="用户加载失败"
                    description={users.error instanceof ApiError ? users.error.friendlyMessage : String(users.error)}
                  />
                ) : null}

                <Table<AdminUserRow>
                  rowKey="id"
                  size="middle"
                  columns={userColumns}
                  dataSource={rows}
                  loading={users.isPending}
                  pagination={false}
                  scroll={{ x: sumWidth(userColumns) }}
                  locale={{ emptyText: '暂无用户' }}
                />

                <Space style={{ marginTop: 16 }}>
                  <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
                  {users.hasNextPage ? (
                    <Button onClick={() => users.fetchNextPage()} loading={users.isFetchingNextPage}>
                      加载更多
                    </Button>
                  ) : null}
                </Space>
              </>
            )
          },
          {
            key: 'roles',
            label: '角色与权限',
            children: (
              <Table<AdminRoleRow>
                rowKey="id"
                size="middle"
                columns={roleColumns}
                dataSource={allRoles}
                loading={roles.isPending}
                pagination={false}
                scroll={{ x: sumWidth(roleColumns) }}
                locale={{ emptyText: '暂无角色（可执行 db/wz-conf/03-sys-rbac-seed.sql 初始化）' }}
              />
            )
          }
        ]}
      />

      <Modal
        title="新建用户"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        footer={null}
        width={560}
        destroyOnHidden
      >
        <Form<CreateValues>
          form={createForm}
          layout="vertical"
          initialValues={{ levelCode: 'L3', status: 'ENABLED' }}
          onFinish={(values) => createUser.mutate(values)}
        >
          <Form.Item
            name="orgId"
            label="所属机构"
            rules={[{ required: true, message: '必选' }]}
            extra={
              platform && !tenantId
                ? '未选租户时机构列表只加载前 200 条，建议先在列表上方选定租户'
                : '机构号同时是账号前缀'
            }
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="输入机构名称 / 机构号快搜"
              loading={orgs.isPending}
              options={orgOptions}
              notFoundContent={orgs.isPending ? '机构加载中…' : '无匹配机构'}
              onChange={() => setEmailCheck(undefined)}
            />
          </Form.Item>

          <Form.Item label="账号" extra={codeHint}>
            <Input
              disabled
              readOnly
              value={preflight.data?.userCode ?? ''}
              placeholder={pickedOrgId ? '生成中…' : '选择机构后自动生成'}
            />
          </Form.Item>

          <Form.Item name="realName" label="姓名" rules={[{ required: true, message: '必填' }]}>
            <Input maxLength={64} />
          </Form.Item>
          <Form.Item
            name="mobile"
            label="手机号"
            rules={[{ pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确' }]}
          >
            <Input maxLength={11} placeholder="11 位手机号" />
          </Form.Item>
          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { required: true, message: '邮箱必填' },
              { type: 'email', message: '邮箱格式不正确' }
            ]}
            extra={
              emailCount > 0 ? (
                <Typography.Text type="warning">
                  已有 {emailCount} 个账号使用该邮箱；邮箱用于重设密码，不强制唯一，但建议一人一箱
                </Typography.Text>
              ) : (
                '邮箱用于重设密码'
              )
            }
          >
            <Input
              type="email"
              maxLength={128}
              placeholder="name@example.com"
              onBlur={(event) => setEmailCheck(event.target.value.trim() || undefined)}
            />
          </Form.Item>
          <Form.Item name="levelCode" label="账号等级" rules={[{ required: true, message: '必选' }]}>
            <Select options={levelOptions} placeholder="选择账号等级" />
          </Form.Item>
          <Form.Item name="password" label="初始密码" rules={[{ required: true, min: 8, message: '至少 8 位' }]}>
            <Input.Password maxLength={64} />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={500} showCount />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={createUser.isPending}>
            创建
          </Button>
        </Form>
      </Modal>

      <Drawer
        title={roleUser ? `分配角色：${roleUser.userCode}` : '分配角色'}
        open={roleUser !== null}
        onClose={() => setRoleUser(null)}
        width={420}
      >
        {roleUser ? (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Typography.Text type="secondary">勾选后立即保存（替换该用户全部角色）。</Typography.Text>
            {allRoles.map((role) => (
              <Space key={role.id} style={{ width: '100%', justifyContent: 'space-between' }}>
                <span>
                  {role.roleName ?? role.roleCode}
                  <Typography.Text type="secondary">（{role.portal ?? '-'} / {role.dataScope ?? '-'}）</Typography.Text>
                </span>
                <Switch
                  checked={boundRoleIds.has(role.id)}
                  loading={setRoles.isPending}
                  onChange={(checked) => {
                    const next = new Set(boundRoleIds);
                    if (checked) next.add(role.id);
                    else next.delete(role.id);
                    setRoles.mutate({ id: roleUser.id, roleIds: Array.from(next) });
                  }}
                />
              </Space>
            ))}
          </Space>
        ) : null}
      </Drawer>

      <Drawer
        title={permRole ? `角色权限：${permRole.roleName ?? permRole.roleCode}` : '角色权限'}
        open={permRole !== null}
        onClose={() => setPermRole(null)}
        width={520}
      >
        {permRole ? (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Checkbox.Group
              style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              value={selectedPerms}
              onChange={(values) => setSelectedPerms(values.map(String))}
            >
              {allPerms.map((perm: AdminPermRow) => (
                <Checkbox key={perm.id} value={perm.id}>
                  {perm.permName ?? perm.permCode}
                  <Typography.Text type="secondary"> · {perm.permCode}</Typography.Text>
                </Checkbox>
              ))}
            </Checkbox.Group>
            <Button
              type="primary"
              block
              loading={setPerms.isPending}
              onClick={() => setPerms.mutate({ id: permRole.id, permIds: selectedPerms })}
            >
              保存权限
            </Button>
            <Typography.Text type="secondary">权限变更在用户下次登录后生效（JWT 内含 perms 声明）。</Typography.Text>
          </Space>
        ) : null}
      </Drawer>
    </Card>
  );
}
